import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, map, of, tap } from 'rxjs';
import { Timeseries } from '../../models/timeseries.model';

@Injectable({
  providedIn: 'root',
})
export class CachedPreviewTimeseriesService {
  constructor(
    private api: ApiService
  ) {}

  private getFromLocalStorage(key: string): any {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  }
  private setToLocalStorage(key: string, value: any): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  getPreviewTimeseries(
    timeSeriesId: string,
    timeSeriesRevisionId: number
  ): Observable<Timeseries> {
    const cachedPreviewData = this.getCachedPreviewData(
      timeSeriesId,
      timeSeriesRevisionId
    );
    if (cachedPreviewData) {
      return of(cachedPreviewData);
    }
    return this.getTimeseriesAndUpdateCache(timeSeriesId, timeSeriesRevisionId);
  }

  getTimeseriesMetrics(
    timeSeriesId: string,
    timeSeriesRevisionId: number,
    startRange: any,
    endRange: any
  ): any {
    const cachedPreviewData = this.getCachedMetricsData(
      timeSeriesId,
      timeSeriesRevisionId
    );
    if (cachedPreviewData) {
      return cachedPreviewData;
    }
    return this.getMetricsAndUpdateCache(timeSeriesId, timeSeriesRevisionId, startRange, endRange);
  }

  getAllimeseries(
    timeSeriesId: string,
    timeSeriesRevisionId: number
  ): Observable<Timeseries> {
    const cachedPreviewData = this.getAllCachedPreviewData(
      timeSeriesId,
      timeSeriesRevisionId
    );
    if (cachedPreviewData) {
      return of(cachedPreviewData);
    }
    return this.getAllTimeseriesAndUpdateCache(timeSeriesId, timeSeriesRevisionId);
  }

  private getTimeseriesPreiviewKey(
    timeSeriesId: string,
    timeSeriesRevisionId: number
  ) {
    return `${timeSeriesId}_${timeSeriesRevisionId}_preview`;
  }

  private getTimeseriesMetricsKey(
    timeSeriesId: string,
    timeSeriesRevisionId: number
  ) {
    return `${timeSeriesId}_${timeSeriesRevisionId}_metrics`;
  }

  private getAllTimeseriesPreiviewKey(
    timeSeriesId: string,
    timeSeriesRevisionId: number
  ) {
    return `${timeSeriesId}_${timeSeriesRevisionId}_all`;
  }

  private getCachedPreviewData(
    timeSeriesId: string,
    timeSeriesRevisionId: number
  ): Timeseries | null {
    const stored = this.getFromLocalStorage(this.getTimeseriesPreiviewKey(timeSeriesId, timeSeriesRevisionId));
    const data = stored?.data;
    if (data) {
      this.setToLocalStorage(
        this.getTimeseriesPreiviewKey(timeSeriesId, timeSeriesRevisionId),
        {
          data,
          lastAccess: Date.now(),
        }
      );
    }
    return data;
  }

  private getCachedMetricsData(
    timeSeriesId: string,
    timeSeriesRevisionId: number
  ): Timeseries | null {
    const data = this.getFromLocalStorage(this.getTimeseriesMetricsKey(timeSeriesId, timeSeriesRevisionId));
    if (data) {
      this.setToLocalStorage(
        this.getTimeseriesMetricsKey(timeSeriesId, timeSeriesRevisionId),
        {
          ...data,
          lastAccess: Date.now(),
        }
      );
    }
    return data;
  }

  private getAllCachedPreviewData(
    timeSeriesId: string,
    timeSeriesRevisionId: number
  ): Timeseries | null {
    const stored = this.getFromLocalStorage(this.getAllTimeseriesPreiviewKey(timeSeriesId, timeSeriesRevisionId));
    const data = stored?.data;
    if (data) {
      this.setToLocalStorage(
        this.getAllTimeseriesPreiviewKey(timeSeriesId, timeSeriesRevisionId),
        {
          data: data.flat(),
          lastAccess: Date.now(),
        }
      );
    }
    return (data && data.length) ? data.flat() : data;
  }

  private getTimeseriesAndUpdateCache(
    timeSeriesId: string,
    timeSeriesRevisionId: number
  ): Observable<Timeseries> {
    return this.api
      .GetTimeSeriesPreview(timeSeriesId, timeSeriesRevisionId)
      .pipe(
        tap((data: Timeseries) =>
          this.setToLocalStorage(
            this.getTimeseriesPreiviewKey(timeSeriesId, timeSeriesRevisionId),
            {
              data,
              lastAccess: Date.now(),
            }
          )
        )
      );
  }

  private getMetricsAndUpdateCache(
    timeSeriesId: string,
    timeSeriesRevisionId: number,
    startRange: any,
    endRange: any
  ): any {
     this.api
      .GetKeyMetricsOfCurve(timeSeriesId, timeSeriesRevisionId, startRange, endRange)
      .subscribe(res => {
        this.setToLocalStorage(
          this.getTimeseriesMetricsKey(timeSeriesId, timeSeriesRevisionId),
          {
            ...res,
            lastAccess: Date.now(),
          }
        )
      })
  }

  private getAllTimeseriesAndUpdateCache(
    timeSeriesId: string,
    timeSeriesRevisionId: any
  ): Observable<Timeseries> {
    return this.api.GetAllTSDataObjects(timeSeriesId, timeSeriesRevisionId).pipe(
      map((data: Timeseries) => {
          return data.flat();
      }),
      tap((data: Timeseries) => {
          // Store the flattened data in local storage
          this.setToLocalStorage(
              this.getAllTimeseriesPreiviewKey(timeSeriesId, timeSeriesRevisionId),
              {
                  data: data.flat(),
                  lastAccess: Date.now(),
              }
          );
      })
  );
  }
}
